import {
  RepeatSchedule,
  QuarterlyRepeatRule,
} from '../../lib/shared/calendar.types'

function getOrdinalSuffix(n: number): string {
  if (n > 3 && n < 21) return 'th'
  switch (n % 10) {
    case 1:
      return 'st'
    case 2:
      return 'nd'
    case 3:
      return 'rd'
    default:
      return 'th'
  }
}

function getQuarterDescription(rule: QuarterlyRepeatRule): string {
  const quarterType = {
    calendar: 'calendar year',
    tax: 'tax year',
    accounting: 'accounting year',
  }[rule.quarterType]

  if (!quarterType) return 'quarterly'

  const monthInQuarter = {
    1: 'first',
    2: 'second',
    3: 'third',
  }[rule.month]

  if (!monthInQuarter) return 'quarterly'

  const dayDescription = `${rule.value}${getOrdinalSuffix(rule.value)}`
  const intervalDescription =
    rule.interval && rule.interval > 1 ? `every ${rule.interval} quarters ` : ''

  return `${intervalDescription}on the ${dayDescription} of the ${monthInQuarter} month of each ${quarterType} quarter`
}

export function getRepeatDescription(schedule: RepeatSchedule): string {
  const { rule } = schedule

  switch (rule.frequency) {
    case 'daily':
      if (rule.skipWeekends) {
        return rule.interval && rule.interval > 1
          ? `Every ${rule.interval} weekdays`
          : 'Every weekday'
      }
      return rule.interval && rule.interval > 1
        ? `Every ${rule.interval} days`
        : 'Daily'

    case 'weekly':
      const days = rule.daysOfWeek
        ?.map(
          (day) =>
            [
              'Sunday',
              'Monday',
              'Tuesday',
              'Wednesday',
              'Thursday',
              'Friday',
              'Saturday',
            ][day],
        )
        .join(', ')
      return rule.interval && rule.interval > 1
        ? `Every ${rule.interval} weeks on ${days}`
        : `Weekly on ${days}`

    case 'monthly':
      if (rule.type === 'dayOfMonth') {
        return rule.interval && rule.interval > 1
          ? `Every ${rule.interval} months on the ${
              rule.value
            }${getOrdinalSuffix(rule.value)}`
          : `Monthly on the ${rule.value}${getOrdinalSuffix(rule.value)}`
      } else {
        const nth =
          rule.value === -1
            ? 'last'
            : ['first', 'second', 'third', 'fourth', 'fifth'][rule.value - 1]
        const day = [
          'Sunday',
          'Monday',
          'Tuesday',
          'Wednesday',
          'Thursday',
          'Friday',
          'Saturday',
        ][rule.dayOfWeek!]
        return rule.interval && rule.interval > 1
          ? `Every ${rule.interval} months on the ${nth} ${day}`
          : `Monthly on the ${nth} ${day}`
      }

    case 'quarterly':
      return getQuarterDescription(rule as QuarterlyRepeatRule)

    case 'annually':
      const month = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
      ][rule.month - 1]

      if (rule.type === 'dayOfMonth') {
        return rule.interval && rule.interval > 1
          ? `Every ${rule.interval} years on ${month} ${rule.value}`
          : `Every year on ${month} ${rule.value}`
      } else {
        const nth =
          rule.value === -1
            ? 'last'
            : ['first', 'second', 'third', 'fourth', 'fifth'][rule.value - 1]
        const day = [
          'Sunday',
          'Monday',
          'Tuesday',
          'Wednesday',
          'Thursday',
          'Friday',
          'Saturday',
        ][rule.dayOfWeek!]
        return rule.interval && rule.interval > 1
          ? `Every ${rule.interval} years on the ${nth} ${day} of ${month}`
          : `Annually on the ${nth} ${day} of ${month}`
      }
  }
}
